import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import {
  Copy,
  DollarSign,
  Gamepad2,
  Hash,
  LogOut,
  Medal,
  Pencil,
  Shield,
  Star,
  Trophy,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/AuthContext';
import { db, storage } from '../../firebase/config';
import { listUserTournaments } from '../../database/participation';
import { deriveStatus } from '../../contexts/TournamentContext';
import MyAnonymousIdentities from './MyAnonymousIdentities';
import ProfileWall from './ProfileWall/ProfileWall';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { countries, getCountry } from '../../lib/countries';
import { modeLabel } from '../../constants/modes';
import { getPlayerSpend, getTiers } from '../../firebase/supporterTiers';
import { resolveTier } from '../../constants/supporterTiers';
import TierBadge from '../ui/TierBadge';
import SEO from '../SEO';
import { absoluteUrl, truncate } from '../../lib/seo';

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const countryFlag = (code) => {
  if (!code || code.length !== 2) return null;
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => c.charCodeAt(0) + 127397));
};

const placeAccent = {
  1: 'text-gold',
  2: 'text-silver',
  3: 'text-bronze',
};

const placeLabel = (p) => (p === 1 ? '1st' : p === 2 ? '2nd' : p === 3 ? '3rd' : `${p}th`);

const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
  </div>
);

const UserProfile = () => {
  const { currentUser, userProfile, logout } = useContext(AuthContext);
  const { userId } = useParams();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [winners, setWinners] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [tournamentAdminData, setTournamentAdminData] = useState([]);
  const [tier, setTier] = useState(null);
  const [hasActiveGuessTournament, setHasActiveGuessTournament] = useState(false);

  const [form, setForm] = useState({
    username: '',
    location: '',
    bio: '',
    steamLink: '',
    discordUsername: '',
  });
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);

  const loadWinners = async (uid) => {
    if (!uid) return;
    try {
      const snap = await getDocs(
        query(collection(db, 'tournamentWinners'), where('userId', '==', uid))
      );
      setWinners(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.place || 0) - (b.place || 0))
      );
    } catch {
      // ignore
    }
  };

  const loadTournamentAdmins = async (uid) => {
    if (!uid) return;
    try {
      const snap = await getDocs(
        query(collection(db, 'tournamentAdmins'), where('userId', '==', uid))
      );
      if (snap.empty) {
        setTournamentAdminData([]);
        return;
      }
      const data = await Promise.all(
        snap.docs.map(async (d) => {
          const info = d.data();
          try {
            const t = await getDoc(doc(db, 'tournaments', info.tournamentId));
            if (t.exists()) return { ...info, tournamentName: t.data().name };
          } catch {
            // fallthrough
          }
          return { ...info, tournamentName: `Tournament ${info.tournamentId}` };
        })
      );
      setTournamentAdminData(data);
    } catch {
      setTournamentAdminData([]);
    }
  };

  const loadParticipations = async (uid) => {
    if (!uid) return;
    try {
      setParticipations(await listUserTournaments(uid));
    } catch {
      setParticipations([]);
    }
  };

  const loadGuessGate = async () => {
    try {
      const snap = await getDocs(
        query(collection(db, 'tournaments'), where('format', '==', 'guess'))
      );
      const active = snap.docs.some((d) => deriveStatus(d.data()) === 'active');
      setHasActiveGuessTournament(active);
    } catch {
      setHasActiveGuessTournament(false);
    }
  };

  const loadTier = async (uid) => {
    if (!uid) return;
    try {
      const [spend, tiers] = await Promise.all([getPlayerSpend(uid), getTiers()]);
      setTier(resolveTier(spend, tiers.length ? tiers : undefined));
    } catch {
      setTier(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        if (userId && userId !== currentUser?.uid) {
          const snap = await getDoc(doc(db, 'userProfiles', userId));
          if (!snap.exists()) {
            if (!cancelled) setProfileData(null);
            return;
          }
          if (!cancelled) {
            setProfileData({ id: userId, ...snap.data() });
            setIsOwnProfile(false);
          }
          await Promise.all([
            loadWinners(userId),
            loadParticipations(userId),
            loadTournamentAdmins(userId),
            loadTier(userId),
          ]);
        } else if (currentUser) {
          if (!cancelled) {
            setProfileData(userProfile);
            setIsOwnProfile(true);
          }
          await Promise.all([
            loadWinners(currentUser.uid),
            loadParticipations(currentUser.uid),
            loadTournamentAdmins(currentUser.uid),
            loadTier(currentUser.uid),
            loadGuessGate(),
          ]);
        }
      } catch (err) {
        toast.error('Could not load profile.', { description: err.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId, currentUser, userProfile]);

  useEffect(() => {
    if (profileData) {
      setForm({
        username: profileData.username || '',
        location: profileData.location || '',
        bio: profileData.bio || '',
        steamLink: profileData.steamLink || '',
        discordUsername: profileData.discordUsername || '',
      });
      setProfilePicPreview(profileData.profilePicUrl || null);
      setProfilePic(null);
    }
  }, [profileData]);

  const handleSave = async () => {
    if (!form.username.trim()) {
      toast.error('Username is required.');
      return;
    }
    const country = getCountry(form.location);
    if (!country) {
      toast.error('Select a country.');
      return;
    }
    setSaving(true);
    try {
      let profilePicUrl = profileData?.profilePicUrl;
      if (profilePic) {
        const sref = ref(storage, `profilePics/${Date.now()}_${profilePic.name}`);
        await uploadBytes(sref, profilePic);
        profilePicUrl = await getDownloadURL(sref);
      }
      const payload = {
        ...form,
        location: country.code,
        countryName: country.name,
        updatedAt: new Date().toISOString(),
      };
      if (profilePicUrl) payload.profilePicUrl = profilePicUrl;
      await updateDoc(doc(db, 'userProfiles', currentUser.uid), payload);
      const fresh = await getDoc(doc(db, 'userProfiles', currentUser.uid));
      if (fresh.exists()) setProfileData({ id: currentUser.uid, ...fresh.data() });
      toast.success('Profile updated.');
      setEditing(false);
    } catch (err) {
      toast.error('Update failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setProfilePic(null);
    if (profileData) {
      setForm({
        username: profileData.username || '',
        location: profileData.location || '',
        bio: profileData.bio || '',
        steamLink: profileData.steamLink || '',
        discordUsername: profileData.discordUsername || '',
      });
      setProfilePicPreview(profileData.profilePicUrl || null);
    }
  };

  const copyDiscord = () => {
    if (!profileData?.discordUsername) return;
    navigator.clipboard
      .writeText(profileData.discordUsername)
      .then(() => toast.success('Discord username copied.'))
      .catch((err) => toast.error('Copy failed.', { description: err.message }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      toast.error('Logout failed.', { description: err.message });
    }
  };

  if (loading && !profileData) {
    return (
      <div className="mx-auto flex max-w-screen-md items-center justify-center px-6 py-24">
        <div className="h-48 w-full animate-pulse rounded-md bg-muted/40" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="mx-auto max-w-screen-md px-6 py-24 text-center">
        <SEO title="Profile not found" noindex />
        <h1 className="font-display text-3xl font-semibold">Profile not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The user you're looking for doesn't exist or was removed.
        </p>
      </div>
    );
  }

  const isSuperAdmin = profileData.role === 'admin';
  const podiums = winners.filter((w) => (w.place || 0) >= 1 && (w.place || 0) <= 3).length;
  const prizeWon = winners.reduce((sum, w) => sum + (Number(w.prizeMoney) || 0), 0);
  const stats = [
    { label: 'Tournaments', value: participations.length },
    { label: 'Podiums', value: podiums },
    { label: 'Prize won', value: prizeWon > 0 ? `$${prizeWon.toLocaleString()}` : '—' },
  ];
  const profileName = profileData.username || 'KZ Player';
  const profileDesc = truncate(
    profileData.bio ||
      `${profileName} — competitive KZ player profile on KZCOMP. Achievements, modes and tournament history.`,
    160
  );
  const profilePath = isOwnProfile ? '/profile' : `/user/${userId || profileData.id}`;
  const profileLd = !isOwnProfile && {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: profileName,
      ...(profileData.bio ? { description: profileData.bio } : {}),
      ...(profileData.profilePicUrl ? { image: profileData.profilePicUrl } : {}),
      url: absoluteUrl(profilePath),
    },
  };

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 pb-24 pt-12 sm:px-6">
      <SEO
        title={isOwnProfile ? 'Your Profile' : profileName}
        description={profileDesc}
        type="profile"
        canonicalPath={profilePath}
        image={profileData.profilePicUrl}
        noindex={isOwnProfile}
        jsonLd={profileLd || undefined}
      />
      <div className="mb-10 flex items-start justify-between gap-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="h-px w-6 bg-heat" />
            {isOwnProfile ? 'Your profile' : 'Player profile'}
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {profileData.username || 'Player'}
          </h1>
        </div>
        {isOwnProfile && !editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              Edit profile
            </Button>
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              Log out
            </Button>
          </div>
        )}
      </div>

      {!editing && (
        <div className="mb-8 grid grid-cols-3 gap-px overflow-hidden rounded-md border border-border/60 bg-border/60">
          {stats.map((s) => (
            <div key={s.label} className="bg-background/40 px-4 py-5 text-center sm:px-6 sm:py-6">
              <div className="font-display text-2xl font-bold tabular-nums sm:text-4xl">
                {s.value}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        {/* Left: avatar + contact */}
        <aside className="space-y-6">
          <div className="flex flex-col items-center gap-4 rounded-md border border-border/60 bg-background/40 p-6 text-center">
            <Avatar className="h-32 w-32">
              {profilePicPreview ? (
                <AvatarImage src={profilePicPreview} alt="" />
              ) : (
                profileData.profilePicUrl && <AvatarImage src={profileData.profilePicUrl} alt="" />
              )}
              <AvatarFallback className="text-2xl">
                {initials(form.username || profileData.username)}
              </AvatarFallback>
            </Avatar>
            {editing ? (
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:border-foreground hover:text-foreground">
                <Upload className="h-3.5 w-3.5" />
                Change picture
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setProfilePic(f);
                      setProfilePicPreview(URL.createObjectURL(f));
                    }
                  }}
                />
              </label>
            ) : (
              <div className="flex flex-wrap justify-center gap-2">
                {isSuperAdmin && (
                  <Badge variant="live" className="uppercase">
                    <Shield className="h-2.5 w-2.5" />
                    Super admin
                  </Badge>
                )}
                {tournamentAdminData.length > 0 && (
                  <Badge variant="outline" className="uppercase">
                    <Star className="h-2.5 w-2.5" />
                    Admin · {tournamentAdminData.length}
                  </Badge>
                )}
                {tier && <TierBadge tier={tier} size="lg" />}
              </div>
            )}

            {!editing && (
              <>
                {profileData.location && (
                  <div className="flex items-center gap-3 text-center">
                    <span className="text-3xl leading-none">
                      {countryFlag(profileData.location)}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      {profileData.location}
                    </span>
                  </div>
                )}

                <div className="flex w-full flex-col gap-2 pt-2">
                  {profileData.steamLink && (
                    <a
                      href={profileData.steamLink}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex items-center justify-center gap-2 rounded-md border border-border/60 px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
                    >
                      <Gamepad2 className="h-3.5 w-3.5 text-info" />
                      Steam profile
                    </a>
                  )}
                  {profileData.discordUsername && (
                    <button
                      type="button"
                      onClick={copyDiscord}
                      className="flex items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
                      title="Copy Discord username"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Hash className="h-3.5 w-3.5 text-info" />
                        {profileData.discordUsername}
                      </span>
                      <Copy className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </aside>

        {/* Right: bio + achievements */}
        <div className="space-y-6">
          {editing ? (
            <Card className="border-border/60 bg-background/40">
              <CardHeader>
                <CardTitle className="font-display text-lg">Edit profile</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-5">
                <Field label="Username" required>
                  <Input
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Country" required>
                    <NativeSelect
                      value={getCountry(form.location)?.code || ''}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                    >
                      <option value="">Select a country…</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                  <Field label="Discord username">
                    <Input
                      value={form.discordUsername}
                      onChange={(e) => setForm({ ...form, discordUsername: e.target.value })}
                    />
                  </Field>
                </div>
                <Field label="Steam profile URL">
                  <Input
                    value={form.steamLink}
                    onChange={(e) => setForm({ ...form, steamLink: e.target.value })}
                    placeholder="https://steamcommunity.com/id/…"
                  />
                </Field>
                <Field label="Bio">
                  <Textarea
                    rows={4}
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    placeholder="Tell others about yourself…"
                  />
                </Field>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                  <Button variant="ghost" onClick={handleCancel} disabled={saving}>
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/60 bg-background/40">
              <CardHeader>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  About
                </div>
                <CardTitle className="font-display text-lg">Bio</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground sm:text-base">
                  {profileData.bio || 'No bio provided.'}
                </p>
              </CardContent>
            </Card>
          )}

          {!editing && tournamentAdminData.length > 0 && (
            <Card className="border-border/60 bg-background/40">
              <CardHeader>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  <Star className="h-3 w-3" />
                  Tournament admin
                </div>
                <CardTitle className="font-display text-lg">
                  {tournamentAdminData.length} tournament
                  {tournamentAdminData.length === 1 ? '' : 's'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {tournamentAdminData.map((a) => (
                    <Badge key={a.tournamentId} variant="outline">
                      {a.tournamentName}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {!editing && participations.length > 0 && (
            <Card className="border-border/60 bg-background/40">
              <CardHeader>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  <Trophy className="h-3 w-3" />
                  Tournaments
                </div>
                <CardTitle className="font-display text-lg">
                  Tournament history · {participations.length}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border/60 border-y border-border/60">
                  {participations.map((p) => {
                    const win = winners.find((w) => w.tournamentId === p.id);
                    const date = toDate(p.endDate) || toDate(p.startDate);
                    return (
                      <li
                        key={p.id}
                        className="grid grid-cols-[1fr_auto] items-center gap-4 px-1 py-3"
                      >
                        <div className="min-w-0">
                          <Link
                            to={`/tournaments/${p.slug || p.id}`}
                            className="truncate font-display text-base font-medium hover:underline"
                          >
                            {p.name}
                          </Link>
                          {date && (
                            <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                              {date.toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        {win && (
                          <div
                            className={cn(
                              'inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em]',
                              placeAccent[win.place] || 'text-muted-foreground'
                            )}
                          >
                            <Medal className="h-3.5 w-3.5" />
                            {placeLabel(win.place || 0)}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          )}

          {!editing && winners.length > 0 && (
            <Card className="border-border/60 bg-background/40">
              <CardHeader>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  <Trophy className="h-3 w-3" />
                  Achievements
                </div>
                <CardTitle className="font-display text-lg">
                  Tournament finishes · {winners.length}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border/60 border-y border-border/60">
                  {winners.map((w) => (
                    <li
                      key={w.id}
                      className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-1 py-3"
                    >
                      <div
                        className={cn(
                          'inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em]',
                          placeAccent[w.place] || 'text-muted-foreground'
                        )}
                      >
                        <Medal className="h-3.5 w-3.5" />
                        {placeLabel(w.place || 0)}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-display text-base font-medium">
                          {w.tournamentName}
                        </div>
                        {w.mode && (
                          <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            {modeLabel(w.mode)}
                          </div>
                        )}
                      </div>
                      {w.prizeMoney > 0 && (
                        <Badge variant="outline" className="text-success">
                          <DollarSign className="h-3 w-3" />
                          {w.prizeMoney}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {!editing && isOwnProfile && hasActiveGuessTournament && <MyAnonymousIdentities />}

          {!editing && profileData?.id && <ProfileWall ownerUserId={profileData.id} />}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
